<script setup>
import { ref, computed, onMounted } from 'vue'
import { api } from '@/api/client'
import { EP } from '@/api/endpoints'
import HeadlineFraunces from '@/design/primitives/HeadlineFraunces.vue'
import IconExternal from '@/design/icons/IconExternal.vue'

const STEAM_HREF = 'https://store.steampowered.com/app/2399830/ARK_Survival_Ascended/'

const FALLBACK_STEPS = [
  { title: 'Launch ARK',     desc: 'Open ARK: Survival Ascended on Steam.', href: STEAM_HREF },
  { title: 'Find Evoluxu',   desc: 'Search for Evoluxu in the server browser.' },
  { title: 'Join',           desc: 'Connect to the server.' },
  { title: 'Create',         desc: 'Spawn your character on the island.' },
  { title: 'Survive',        desc: 'Start your story.' },
]

const rawSteps = ref(FALLBACK_STEPS)

const STEPS = computed(() => rawSteps.value.map((s, i) => ({
  num: String(i + 1).padStart(2, '0'),
  title: s.title || '',
  body: s.desc || s.body || '',
  href: i === 0 ? (s.href || STEAM_HREF) : (s.href || null),
})))

onMounted(async () => {
  try {
    const { data } = await api.get(EP.siteContent)
    if (Array.isArray(data?.howItWorks) && data.howItWorks.length > 0) {
      rawSteps.value = data.howItWorks
    }
  } catch {}
})
</script>

<template>
  <section id="how-to-play" class="howto">
    <div class="howto__container">
      <div class="howto__head">
        <span class="howto__kicker">EVOLUXU · ONBOARDING</span>
        <HeadlineFraunces level="h2">How to <em>Start</em> Playing</HeadlineFraunces>
      </div>

      <ol class="howto__grid">
        <li v-for="step in STEPS" :key="step.num" class="howto__step">
          <span class="howto__num">{{ step.num }}</span>
          <span class="howto__rule" aria-hidden="true"></span>
          <h3 class="howto__title">
            {{ step.title }}
            <a v-if="step.href" :href="step.href" target="_blank" rel="noopener" class="howto__link" aria-label="Open Steam page">
              <IconExternal class="howto__link-icon"/>
            </a>
          </h3>
          <p class="howto__body">{{ step.body }}</p>
        </li>
      </ol>
    </div>
  </section>
</template>

<style scoped>
.howto {
  padding: var(--sp-24) var(--sp-12);
}
.howto__container {
  max-width: var(--container-wide);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--sp-12);
}

.howto__head {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  text-align: center;
  align-items: center;
}
.howto__kicker {
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--gold);
}

.howto__grid {
  list-style: none;
  margin: 0;
  padding: 0;
  /* Flex (not a fixed-column grid) so the cards stay centered for ANY step
     count — admins commonly trim the default 5 steps down to 3, which left a
     5-col grid hugging the left edge. */
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--sp-3);
}

.howto__step {
  position: relative;
  flex: 1 1 200px;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-5, 20px) var(--sp-4) var(--sp-4);
  background: rgba(11, 10, 8, 0.55);
  border: 1px solid var(--ash);
  border-radius: var(--r-sm);
  text-align: center;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  transition: border-color var(--t-base) var(--ease-cinema),
              transform var(--t-base) var(--ease-cinema),
              background var(--t-base) var(--ease-cinema);
}
.howto__step::before {
  content: "";
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--gold) 50%, transparent);
  opacity: 0.4;
}
.howto__step:hover {
  border-color: var(--gold);
  background: rgba(11, 10, 8, 0.78);
  transform: translateY(-3px);
}
.howto__step:hover::before { opacity: 0.95; }

.howto__num {
  font-family: var(--font-display);
  font-size: clamp(36px, 3.4vw, 52px);
  line-height: 0.9;
  color: var(--gold);
}
.howto__rule {
  align-self: stretch;
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, var(--gold) 25%, var(--gold) 75%, transparent 100%);
  opacity: 0.55;
  margin: var(--sp-1) 0;
}
.howto__title {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--fs-16);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink);
  font-weight: 400;
}
.howto__link { display: inline-flex; color: var(--gold); transition: color var(--t-fast) var(--ease-road); }
.howto__link:hover { color: var(--gold-deep); }
.howto__link-icon { width: 14px; height: 14px; }

.howto__body {
  margin: 0;
  font-size: var(--fs-14);
  line-height: 1.5;
  color: var(--ink-soft);
}

@media (max-width: 1100px) {
  .howto { padding: var(--sp-16) var(--sp-6); }
}
@media (max-width: 720px) {
  .howto__step { flex-basis: 100%; max-width: none; }
}
</style>
